import { createStore } from "zustand/vanilla";

import type { CameraPlan, RouteGeometry } from "../studio/cameraPlan";
import { geometriesEqual } from "../studio/geometry";
import type { ImportSession, ItineraryDraft } from "./importItinerary";
import {
  type SuggestionCandidate,
  type SuggestionSession,
  selectedCandidate,
} from "./suggestItinerary";
import { type ActiveDraftSliceActions, createActiveDraftSlice } from "./ui/activeDraftSession";
import {
  type ChromeSliceActions,
  type ChromeSliceState,
  chromeInitialState,
  createChromeSlice,
  type ProfileSheetSnap,
} from "./ui/chrome";
import {
  type CollabSliceActions,
  type CollabSliceState,
  collabInitialState,
  createCollabSlice,
} from "./ui/collab";
import {
  type CollectionsSliceActions,
  type CollectionsSliceState,
  collectionsInitialState,
  createCollectionsSlice,
} from "./ui/collections";
import {
  createDirectionsSlice,
  type DirectionsSliceActions,
  type DirectionsSliceState,
  directionsInitialState,
} from "./ui/directions";
import {
  createEmbedSlice,
  type EmbedSliceActions,
  type EmbedSliceState,
  embedInitialState,
} from "./ui/embed";
import {
  createEmbedContentSlice,
  type EmbedContentSliceActions,
  type EmbedContentSliceState,
  embedContentInitialState,
} from "./ui/embedContent";
import {
  createImportSessionSlice,
  type ImportSessionSliceActions,
  type ImportSessionSliceState,
  importSessionInitialState,
} from "./ui/importSession";
import {
  createItinerarySlice,
  type ItinerarySliceActions,
  type ItinerarySliceState,
  itineraryInitialState,
} from "./ui/itinerary";
import {
  createItineraryDuplicateSlice,
  type ItineraryDuplicateSliceActions,
  type ItineraryDuplicateSliceState,
  itineraryDuplicateInitialState,
} from "./ui/itineraryDuplicate";
import {
  createLayersSlice,
  type LayersSliceActions,
  type LayersSliceState,
  layersInitialState,
} from "./ui/layers";
import {
  createSearchSlice,
  type SearchSliceActions,
  type SearchSliceState,
  searchInitialState,
} from "./ui/search";
import {
  createSegmentEditSlice,
  type SegmentEditSliceActions,
  type SegmentEditSliceState,
  type SegmentViaPrompt,
  segmentEditInitialState,
} from "./ui/segmentEdit";
import {
  createSegmentGroupEditSlice,
  type SegmentGroupEditSliceActions,
  type SegmentGroupEditSliceState,
  segmentGroupEditInitialState,
} from "./ui/segmentGroupEdit";
import {
  createSegmentSplitSlice,
  type SegmentSplitSliceActions,
  type SegmentSplitSliceState,
  segmentSplitInitialState,
} from "./ui/segmentSplit";
import {
  createShareSlice,
  type ShareSliceActions,
  type ShareSliceState,
  shareInitialState,
} from "./ui/share";
import {
  createSuggestSessionSlice,
  type SuggestSessionSliceActions,
  type SuggestSessionSliceState,
  suggestSessionInitialState,
} from "./ui/suggestSession";
import {
  createVideoExportSlice,
  type VideoExport,
  type VideoExportSliceActions,
  type VideoExportSliceState,
  type VideoExportStatus,
  videoExportInitialState,
} from "./ui/videoExport";
import {
  createWaypointEditSlice,
  type WaypointEditSliceActions,
  type WaypointEditSliceState,
  waypointEditInitialState,
} from "./ui/waypointEdit";
import {
  createZoneEditSlice,
  type ZoneEditSliceActions,
  type ZoneEditSliceState,
  zoneEditInitialState,
} from "./ui/zoneEdit";

export {
  type AgentHandoff,
  type ContextMenu,
  type ContextMenuZone,
  contextMenuSchema,
  type MapAlert,
  mapAlertSchema,
  type ProfileSheetSnap,
  profileSheetSnapSchema,
  type StreetviewPosition,
  type StudioContent,
  type StudioModal,
  type StudioPanel,
  selectAgentEntityLocked,
  streetviewPositionSchema,
  studioContentSchema,
  studioModalSchema,
  studioPanelSchema,
} from "./ui/chrome";
export {
  COLLAB_PEER_COLORS,
  type CollabLockEntry,
  type CollabLockKind,
  type CollabPeer,
  type CollabPeerCursor,
  type CollabPeerDraft,
  type CollabPresencePeer,
  type CollabResource,
  type CollabResourceKind,
  type CollabResourceState,
  collabLockEntrySchema,
  collabLockKey,
  collabLockKindSchema,
  collabPeerColor,
  collabPeerCursorSchema,
  collabPresencePeerSchema,
  collabResourceKey,
  collabResourceKindSchema,
  collabResourceSchema,
  peerSegmentDraftsOf,
  selectCollabLockHolder,
  selectCollabPeerDrafts,
  selectCollabPeers,
} from "./ui/collab";
export {
  type CollectionsTarget,
  collectionsTargetSchema,
  type EditingCollection,
  type ExportTarget,
  editingCollectionSchema,
  exportTargetSchema,
  type SharingCollection,
  sharingCollectionSchema,
} from "./ui/collections";
export { type DirectionsTarget, directionsTargetSchema, selectDirections } from "./ui/directions";
export { type EmbedTarget, selectEmbed } from "./ui/embed";
export { type EmbedContent, selectEmbedContent } from "./ui/embedContent";
export { type ItinerarySelection, itinerarySelectionSchema } from "./ui/itinerary";
export {
  type ItineraryDuplicateTarget,
  itineraryDuplicateTargetSchema,
  selectItineraryDuplicate,
} from "./ui/itineraryDuplicate";
export {
  type DraftSegmentParams,
  type SegmentEditBegin,
  type SegmentEditMode,
  type SegmentEditSession,
  type SegmentInsertAnchor,
  type SegmentViaPrompt,
  segmentEditBeginSchema,
  segmentEditModeSchema,
  segmentInsertAnchorSchema,
  segmentViaPromptSchema,
  synthesizeDraftSegment,
} from "./ui/segmentEdit";
export {
  type SegmentGroupEditTarget,
  segmentGroupEditTargetSchema,
  selectSegmentGroupEdit,
} from "./ui/segmentGroupEdit";
export {
  type SegmentSplitPick,
  segmentSplitPickSchema,
  selectSegmentSplitPick,
} from "./ui/segmentSplit";
export { type ShareTarget, selectShare } from "./ui/share";
export {
  DEFAULT_VIDEO_EXPORT_PROFILE,
  type VideoExport,
  type VideoExportProfile,
  type VideoExportStatus,
  videoExportProfileSchema,
  videoExportSchema,
  videoExportStatusSchema,
} from "./ui/videoExport";
export {
  type DraftWaypointParams,
  synthesizeDraftWaypoint,
  type WaypointDraft,
  type WaypointEditBegin,
  type WaypointEditIntent,
  type WaypointEditMode,
  type WaypointEditSession,
  type WaypointPoiSeed,
  type WaypointSnapshot,
  waypointDraftSchema,
  waypointEditIntentSchema,
  waypointEditModeSchema,
  waypointPoiSeedSchema,
} from "./ui/waypointEdit";
export {
  DEFAULT_ZONE_CREATE_METHOD,
  DEFAULT_ZONE_LEVEL,
  DEFAULT_ZONE_MODE,
  type ZoneCreateMethod,
  type ZoneCreateSeed,
  type ZoneEditBegin,
  type ZoneEditLevel,
  type ZoneEditMode,
  type ZoneEditSession,
  type ZonePreview,
  type ZonePreviewBand,
  zoneCreateMethodSchema,
  zoneEditBeginSchema,
  zoneEditModeSchema,
  zoneLevelSchema,
  zonePreviewQueryKey,
} from "./ui/zoneEdit";

/**
 * UiState is the studio client/UI slice: the active enrichment overlay slugs
 * stacked on top of the base overlay (e.g. hillshade), the base-layer terrain
 * slug and its light/dark variant, the focused itinerary entity (a segment or a
 * waypoint; null = nothing focused, so the whole itinerary shows), the active
 * itinerary, the open floating panel (null = none), the active map overlay
 * (null = none), the overlay timestamp shared by time-aware overlays (RFC3339;
 * null = now), the open Street View position (null = closed), the open export
 * target (null = the export modal is closed), the itinerary whose
 * manage-collections modal is open (null = closed), the open studio sidenav
 * body slot (null = none — the grouped tree shows), the open studio modal slot
 * (null = no modal is open), the active segment geometry edit session (null =
 * not editing a segment), and the active waypoint authoring session (null =
 * not editing a waypoint). The two edit-session slots are mutually exclusive
 * by tool-layer policy (the editBegin handlers reject opening a second session
 * while either is already armed); the store itself stays synchronous and
 * trustful, so the discriminated `selectActiveEditEngine` selector is the
 * canonical "an edit engine is running" predicate. The terrain slug is a free
 * string resolved against the layers manifest at render time, so no value
 * enum constrains it here. Server state never lives here — it stays in the
 * query cache.
 */
export interface UiState
  extends ChromeSliceState,
    CollabSliceState,
    CollectionsSliceState,
    DirectionsSliceState,
    EmbedContentSliceState,
    EmbedSliceState,
    ImportSessionSliceState,
    ItineraryDuplicateSliceState,
    ItinerarySliceState,
    LayersSliceState,
    SearchSliceState,
    SegmentEditSliceState,
    SegmentGroupEditSliceState,
    SegmentSplitSliceState,
    ShareSliceState,
    SuggestSessionSliceState,
    VideoExportSliceState,
    WaypointEditSliceState,
    ZoneEditSliceState {}

/**
 * ActiveEditEngine is the discriminated readout of which authoring engine is
 * armed, or null when none is. The kind names the engine the map adapter has
 * built (directions / linestring on the segment side; point / poi-pick on the
 * waypoint side) and the matching id ties it to the session's target. It is
 * derived from the two session slots by `selectActiveEditEngine`, so every
 * caller (the navigation guard, the base-map lock, the mutual-exclusion check
 * in the editBegin tools) reads the same single source of truth.
 */
export type ActiveEditEngine =
  | { kind: "directions"; segmentId: string }
  | { kind: "linestring"; segmentId: string }
  | { kind: "point"; waypointId: string }
  | { kind: "poi-pick"; waypointId: string }
  | { kind: "zone"; zoneId: string };

/**
 * UiActions are reached by app components only through the tool bus, never by
 * importing this store directly (the one-mutation-surface invariant).
 */
export interface UiActions
  extends ActiveDraftSliceActions,
    ChromeSliceActions,
    CollabSliceActions,
    CollectionsSliceActions,
    DirectionsSliceActions,
    EmbedContentSliceActions,
    EmbedSliceActions,
    ImportSessionSliceActions,
    ItineraryDuplicateSliceActions,
    ItinerarySliceActions,
    LayersSliceActions,
    SearchSliceActions,
    SegmentEditSliceActions,
    SegmentGroupEditSliceActions,
    SegmentSplitSliceActions,
    ShareSliceActions,
    SuggestSessionSliceActions,
    VideoExportSliceActions,
    WaypointEditSliceActions,
    ZoneEditSliceActions {}

export type UiStore = UiActions & UiState;

const initialState: UiState = {
  ...chromeInitialState,
  ...collabInitialState,
  ...collectionsInitialState,
  ...directionsInitialState,
  ...embedContentInitialState,
  ...embedInitialState,
  ...importSessionInitialState,
  ...itineraryDuplicateInitialState,
  ...itineraryInitialState,
  ...layersInitialState,
  ...searchInitialState,
  ...segmentEditInitialState,
  ...segmentGroupEditInitialState,
  ...segmentSplitInitialState,
  ...shareInitialState,
  ...suggestSessionInitialState,
  ...videoExportInitialState,
  ...waypointEditInitialState,
  ...zoneEditInitialState,
};

/**
 * EMBED_UI_BASELINE is the initial-state override the read-only embed family
 * (the public /studio/embed iframe and the /studio/share surface) builds its
 * store with: no enrichment overlays and no POI groups.
 *
 * The default state seeds the POIs source so the in-app studio paints its
 * default-on groups on the first frame. That seed is wrong for an embed: the POI
 * control (PoiOverlayMenu) mounts only in the in-app studio, the layers panel
 * withholds the POIs switch, and both POI tools declare the "authenticated"
 * capability the embed family does not hold — so a visitor gets a tile source
 * with no way to see or steer it. The tile API refuses it too: an embed
 * credential resolves to a non-root org, where an empty overlay allowlist (the
 * shipped org default) forbids every overlay tile. Seeding both fields empty
 * keeps the store's `"pois" ∈ activeEnrichments ⇔ activePoiGroups.length > 0`
 * invariant intact while the source is never requested.
 */
export const EMBED_UI_BASELINE: Partial<UiState> = {
  activeEnrichments: [],
  activePoiGroups: [],
};

/**
 * createUiStore builds the vanilla studio UI store. Slices are composed into a
 * single store (selector granularity, not store count, governs re-renders).
 *
 * `overrides` patches the initial state at construction — the seam the embed
 * family uses to start from {@link EMBED_UI_BASELINE} rather than the in-app
 * defaults. It is applied before the slices so an override can never shadow an
 * action, and it is a CONSTRUCTION input, not a mutation: every write after this
 * point still crosses the tool bus.
 */
export const createUiStore = (overrides?: Partial<UiState>) =>
  createStore<UiStore>((set, get, store) => ({
    ...initialState,
    ...overrides,
    ...createActiveDraftSlice(set, get, store),
    ...createChromeSlice(set, get, store),
    ...createCollabSlice(set, get, store),
    ...createCollectionsSlice(set, get, store),
    ...createDirectionsSlice(set, get, store),
    ...createEmbedContentSlice(set, get, store),
    ...createEmbedSlice(set, get, store),
    ...createImportSessionSlice(set, get, store),
    ...createItineraryDuplicateSlice(set, get, store),
    ...createItinerarySlice(set, get, store),
    ...createLayersSlice(set, get, store),
    ...createSearchSlice(set, get, store),
    ...createSegmentEditSlice(set, get, store),
    ...createSegmentGroupEditSlice(set, get, store),
    ...createSegmentSplitSlice(set, get, store),
    ...createShareSlice(set, get, store),
    ...createSuggestSessionSlice(set, get, store),
    ...createVideoExportSlice(set, get, store),
    ...createWaypointEditSlice(set, get, store),
    ...createZoneEditSlice(set, get, store),
  }));

export type UiStoreApi = ReturnType<typeof createUiStore>;

/**
 * selectActiveEditEngine is the canonical "an authoring engine is armed"
 * predicate, derived from the three edit-session slots. The discriminated union
 * names the engine kind (directions / linestring on the segment side, point /
 * poi-pick on the waypoint side, zone on the zone side) and the id the session
 * is keyed on, so a caller can branch on the engine type without inspecting the
 * underlying session shape. The slots are checked in a fixed order — segment,
 * then waypoint, then zone — so one session takes precedence if a bug landed
 * more than one at once (which the tool layer's mutual-exclusion prevents in
 * practice, but the selector still answers truthfully); the `null` return is
 * the "no engine armed" floor every guard reads. Narrow by design so the
 * navigation guard's effect re-runs only on the engine flip, not every gesture
 * inside a session.
 */
export const selectActiveEditEngine = (state: UiState): ActiveEditEngine | null => {
  const segment = state.segmentEditSession;
  if (segment) {
    return segment.mode === "directions"
      ? { kind: "directions", segmentId: segment.segmentId }
      : { kind: "linestring", segmentId: segment.segmentId };
  }

  const waypoint = state.waypointEditSession;
  if (waypoint) {
    return waypoint.mode === "point"
      ? { kind: "point", waypointId: waypoint.waypointId }
      : { kind: "poi-pick", waypointId: waypoint.waypointId };
  }

  const zone = state.zoneEditSession;
  if (zone) {
    return { kind: "zone", zoneId: zone.zoneId };
  }

  return null;
};

/**
 * selectProfileSheetSnap is how far the compact elevation sheet is opened. Narrow
 * by design so the sheet and the camera-fit padding re-read only on a snap change,
 * never on a draft push or a hover inside the profile.
 */
export const selectProfileSheetSnap = (state: UiState): ProfileSheetSnap => state.profileSheetSnap;

/**
 * selectSegmentViaPrompt is the open ask-the-user prompt for a clicked handle of
 * the segment being edited, or null when none is open. Narrow by design so the
 * popup re-renders on the prompt opening, moving, or clearing — never on a draft
 * push inside the same edit. The slot holds a fresh object per open, so a consumer
 * subscribes through useShallow to settle on an unchanged index + anchor.
 */
export const selectSegmentViaPrompt = (state: UiState): SegmentViaPrompt | null =>
  state.segmentViaPrompt;

/**
 * selectActiveDraftSession is the open session that currently owns an editable
 * itinerary draft, or null when neither does. It resolves the import session when
 * it holds a draft, else the suggest session when it holds one — the two are
 * mutually exclusive by the openContent body slot, so at most one carries a draft
 * at a time and the check order only disambiguates a tool-layer-impossible
 * double-open. It is the neutral "which flow's draft is live" reader the draft
 * mutation sink resolves through, so a draft edit reaches whichever flow opened
 * the draft without the sink knowing which one.
 */
export const selectActiveDraftSession = (
  state: UiState,
): ImportSession | SuggestionSession | null => {
  if (state.importSession?.draft) {
    return state.importSession;
  }

  if (state.suggestSession?.draft) {
    return state.suggestSession;
  }

  return null;
};

/**
 * selectActiveDraft is the editable itinerary draft on the active draft session,
 * or null when neither session holds one. It is the single predicate the mutation
 * sink resolver reads to decide whether a draft edit (the DraftSink) or a
 * persisted edit (the RestSink) is in play.
 */
export const selectActiveDraft = (state: UiState): ItineraryDraft | null =>
  selectActiveDraftSession(state)?.draft ?? null;

/**
 * selectBaseMapLocked derives whether the base map is locked from the active
 * authoring engine: any session (segment, waypoint, OR zone) locks the base-map
 * controls (terrain switch, and map pan via the edit-lock the map adapter
 * applies) until the session commits or cancels. Narrow selector so the
 * layers panel re-renders only when the locked-ness flips, not on every draft
 * push or session progression. Equivalent to
 * `selectActiveEditEngine(state) !== null`; kept as a named export because the
 * call site reads as a boolean predicate.
 */
export const selectBaseMapLocked = (state: UiState): boolean =>
  selectActiveEditEngine(state) !== null;

/**
 * selectZoneEditDirty reports whether the active zone edit has unsaved geometry
 * changes — the predicate the panel's Save button reads to enable/disable itself.
 * It is false when no zone session is armed. For a session WITH a saved baseline
 * (an edit of a saved zone, or a create after its first save advanced the
 * baseline) it is true when the live draft diverges from that baseline by a
 * structural geometry compare, so dragging a vertex and dragging it back lands
 * back at not-dirty. For a fresh create with NO baseline yet (null saved
 * geometry) the dirty signal is simply "a shape has been drawn" — the first draw
 * is the change a Save persists. Narrow by design so the Save button re-renders
 * only on the dirty flip, not on every intermediate draft push.
 */
export const selectZoneEditDirty = (state: UiState): boolean => {
  const session = state.zoneEditSession;
  if (!session) {
    return false;
  }

  // A fresh create has no persisted baseline: any drawn shape is the unsaved
  // change. Once a baseline exists (a saved zone's geometry, or the shape the last
  // save persisted), dirtiness is the draft diverging from it.
  if (session.savedGeometry === null) {
    return session.draft !== null;
  }

  return !geometriesEqual(session.draft, session.savedGeometry);
};

/**
 * selectZoneCreateSetup reports whether the zone surface is showing the create
 * setup — the step where the user is choosing an organization, a method, and
 * (for a reachability zone) an origin, an activity and a budget. It is true only
 * when the zone surface is open with no zone bound and no draw session armed, so
 * it flips false the moment a create arms its draw engine or a zone binds.
 *
 * It is a NAMED selector rather than an inline predicate because two surfaces
 * must agree on it: the panel branches on it, and the co-pilot derives itself
 * closed from it so the map is unobstructed while a zone is being placed. An
 * inline copy in either place would let the two drift, and the failure would be
 * a co-pilot that stays open over the map the user is trying to click.
 */
export const selectZoneCreateSetup = (state: UiState): boolean =>
  state.openContent === "zone" && state.activeZone === null && state.zoneEditSession === null;

/**
 * selectEditDirtyTick is the monotonic counter the elevation panel subscribes to
 * so the model recomputes on every edit-cycle mutation — including the
 * rapid-undo/redo and throttled live-segment writes that may not flip the
 * segments array identity. Narrow by design: consumers add it to a useMemo's
 * deps and the bump alone forces the fresh compute.
 */
export const selectEditDirtyTick = (state: UiState): number => state.editDirtyTick;

/**
 * selectVideoExport is the open video export wizard state, or null when the
 * wizard is closed. The mount-guard selector a wizard surface reads to decide
 * whether to render at all — narrow so the guard re-renders only on the
 * open/close flip.
 */
export const selectVideoExport = (state: UiState): VideoExport | null => state.videoExport;

/**
 * selectVideoExportPlan is the Camera Plan on the open wizard, or null when the
 * wizard is closed. Narrow by design so the preview surface and the controls
 * re-render when the plan changes (a preset swap, a camera edit) without
 * subscribing to the wizard's progress or step.
 */
export const selectVideoExportPlan = (state: UiState): CameraPlan | null =>
  state.videoExport?.plan ?? null;

/**
 * selectVideoExportRoute is the flattened route the open wizard's Camera Plan
 * resolves against, or null when the wizard is closed or its itinerary detail
 * has not loaded yet. Narrow by design so the preview's framing re-runs when the
 * route lands without subscribing to the plan, step, or progress.
 */
export const selectVideoExportRoute = (state: UiState): RouteGeometry | null =>
  state.videoExport?.route ?? null;

/**
 * selectVideoExportStatus is the preview/recording progress on the open wizard,
 * or null when the wizard is closed. Narrow so the progress UI re-renders on a
 * progress tick without pulling the whole wizard state.
 */
export const selectVideoExportStatus = (state: UiState): VideoExportStatus | null =>
  state.videoExport?.status ?? null;

/**
 * selectImportSession is the open import shell state, or null when the shell is
 * closed. The mount-guard selector an import surface reads to decide whether to
 * render at all — narrow so the guard re-renders only on the open/close flip,
 * mirroring selectVideoExport.
 */
export const selectImportSession = (state: UiState): ImportSession | null => state.importSession;

/**
 * selectSuggestionSession is the open suggest shell state, or null when the
 * shell is closed. The mount-guard selector a suggest surface reads to decide
 * whether to render at all — narrow so the guard re-renders only on the
 * open/close flip, mirroring selectImportSession.
 */
export const selectSuggestionSession = (state: UiState): SuggestionSession | null =>
  state.suggestSession;

/**
 * selectSelectedSuggestionCandidate is the suggest candidate the previewed
 * selection names, or null. It is the source the docked elevation panel reads to
 * show a selected candidate's profile BEFORE the user starts from it. It returns
 * null when a draft is open — a started-from draft is the active draft and wins
 * the view fallback through selectActiveDraft, so the candidate preview steps
 * aside once a draft exists — and null when no suggest session is open or no
 * candidate is selected. Narrow + derived so the panel re-renders only on a
 * selection change while the suggest surface is open, not on every store
 * mutation.
 */
export const selectSelectedSuggestionCandidate = (state: UiState): SuggestionCandidate | null => {
  const session = state.suggestSession;
  if (!session || session.draft) {
    return null;
  }

  return selectedCandidate(session);
};
